import type { ShowSessionConfigOptions } from "@contexts/DialogContext/types";
import {
	type HydratedRecommendationState,
	selectScopedStudyPresentation,
} from "@features/dashboard/recommendations/hooks/useHydratedStudyRecommendations";
import type { RecommendationResult } from "@features/dashboard/recommendations/types";
import {
	getItemQuestionType,
	getItemScore,
	isAnswered,
	isItemAnsweredCorrectly,
} from "@memorang/helpers/item";
import { getQuestionItemsForSkillFilter } from "@memorang/helpers/report";
import type { BaseItemSchema, MixedItemTag } from "@memorang/types";
import { flattenSessionItemsForLookup } from "@memorang/ui/components/SessionReport/SessionReportQuestionTypeCard/helpers";

/**
 * Options for the Quick Study dialog opened when no hydrated recommendation is
 * available. A formative report reopens the filters the session itself used; a
 * summative report has no meaningful filters to carry over.
 */
export const buildNextActionFallbackOptions = ({
	isSummativeTest,
	sessionTagIds,
}: {
	isSummativeTest: boolean;
	sessionTagIds: number[];
}): ShowSessionConfigOptions =>
	isSummativeTest || sessionTagIds.length === 0
		? {}
		: { selectedTagIds: sessionTagIds };

/** Share of points on a point-scale item, otherwise plain correctness. */
const answeredItemScore = (sessionItem: BaseItemSchema): number => {
	const { score, maxScore } = getItemScore(sessionItem);
	return maxScore != null && maxScore > 0
		? Math.min(score / maxScore, 1)
		: Number(isItemAnsweredCorrectly(sessionItem));
};

export const getWeakestQuestionTypeId = ({
	tags,
	sessionItems,
}: {
	tags: MixedItemTag[];
	sessionItems: BaseItemSchema[];
}): number | undefined => {
	const sessionItemById = new Map(
		flattenSessionItemsForLookup(sessionItems)
			.filter((item) => item.id != null)
			.map((item) => [item.id, item]),
	);
	const scoresByType = new Map<number, number[]>();
	for (const { id } of getQuestionItemsForSkillFilter(tags, "all")) {
		const sessionItem = sessionItemById.get(id);
		const typeId = sessionItem && getItemQuestionType(sessionItem)?.id;
		if (sessionItem && typeId !== undefined && isAnswered(sessionItem.answer)) {
			scoresByType.set(typeId, [
				...(scoresByType.get(typeId) ?? []),
				answeredItemScore(sessionItem),
			]);
		}
	}
	let weakest: { id: number; score: number } | undefined;
	for (const [id, scores] of scoresByType) {
		const score =
			scores.reduce((total, value) => total + value, 0) / scores.length;
		if (!weakest || score < weakest.score) {
			weakest = { id, score };
		}
	}
	return weakest && weakest.score < 1 ? weakest.id : undefined;
};

/**
 * Scopes the report's recommendation to the session's weakest question type
 * when the engine can launch a session there; otherwise keeps the skill-wide
 * pick the hydrated state already ranked.
 */
export const resolveNextActionPresentation = ({
	state,
	skillTagIds,
	weakestQuestionTypeId,
}: {
	state: HydratedRecommendationState;
	skillTagIds: number[];
	weakestQuestionTypeId?: number;
}): RecommendationResult | undefined => {
	if (state.status !== "READY") {
		return undefined;
	}
	const scoped =
		weakestQuestionTypeId === undefined
			? undefined
			: selectScopedStudyPresentation({
					state,
					filterValueIds: [...skillTagIds, weakestQuestionTypeId],
					sessionCount: 1,
				});
	return scoped?.status === "READY" && scoped.sessions.length > 0
		? scoped
		: state.presentation;
};
